import { useState, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { IShipment, ICarrierCompliance, ICarrierApplication } from 'types'
import { Maybe } from 'types/graphqlTypes'
import {
  fetchMarketplaceLoad,
  fetchMarketplaceCarrierApplication,
  updateMarketplaceLoadRateCon,
  checkMarketplaceShipmentCarrierCompliance,
  deleteMarketplaceBid,
  useCreateMarketplaceBid,
  useUpdateMarketplaceBid,
  IMarketplaceBid,
  TSignRateConfirmationBody,
} from 'utils/api/marketplace'
import { useFetchData } from 'utils/hooks/useFetchData'
import { AuctionShipmentOpsRep } from 'apps/fleet-planner/types'
import { updateLoadAssignmentSuccess } from 'apps/fleet-planner/utils/actions/loadDetails'
import {
  updateTractor,
  updateShipmentAssignment as updateAssignment,
  IUpdatedAssignmentProps,
} from 'apps/fleet-planner/utils/api'
import * as Api from 'apps/fleet-planner/utils/api/loadDetails'
import { useFeatureFlagContext } from '../context/FeatureFlagsContext'
import { useDriverLocation } from './useDriverLocation'

const defaultOpsRep = {
  phone: '',
  name: '',
  email: '',
}

// The error message marketplace returns when an existing load has already been booked.
const LOAD_UNAVAILABLE_MSG = `Error: You do not have permission`

export const useMarketplace = () => {
  const { getFlag } = useFeatureFlagContext()
  const { enabled: fmsCombinePodFiles } = getFlag('fmsCombinePodFiles')

  const { errors, fetchData, isLoading } = useFetchData()
  const {
    fetchData: fetchCarrierApplicationData,
    isLoading: isLoadingCarrierApplication,
  } = useFetchData()
  const {
    fetchData: fetchShipmentCarrierComplianceData,
    isLoading: isLoadingShipmentCarrierCompliance,
  } = useFetchData()
  const [shipmentDetails, setShipmentDetails] = useState<IShipment>({} as IShipment)
  const [activeShipmentDetailsId, setActiveShipmentDetailsId] = useState<number | undefined>(
    undefined
  )
  const [activeOpsRep, setActiveOpsRep] = useState<AuctionShipmentOpsRep>(defaultOpsRep)
  const [carrierApplication, setCarrierApplication] = useState<Maybe<ICarrierApplication>>()
  const [carrierCompliance, setCarrierCompliance] = useState<Maybe<ICarrierCompliance>>()
  const [updateError, setUpdateError] = useState<string | undefined>(undefined)
  const dispatch = useDispatch()

  const [rateConSigned, setRateConSigned] = useState<boolean>(false)
  const driverLocation = useDriverLocation({
    shipment: shipmentDetails,
  })

  const { mutate: createMarketplaceBidMutation } = useCreateMarketplaceBid({})
  const { mutate: updateMarketplaceBidMutation } = useUpdateMarketplaceBid({})

  const getMarketplaceLoad = useCallback(
    async (id: string) => {
      // strip all non-numeric value from the id for BE fetch request
      // e.g: "TFX-", "-d", etc
      const marketplaceId = id.replace(/\D/g, '')
      const { shipment } = (await fetchData(fetchMarketplaceLoad(marketplaceId))) || {}
      const { info, opsRep } = shipment || {}
      const { carrierSignedAt } = info || {}
      if (opsRep) {
        setActiveOpsRep(opsRep)
      }
      if (carrierSignedAt) {
        setRateConSigned(true)
      }
      setShipmentDetails(shipment)
      return shipment
    },
    [fetchData]
  )

  const getMarketplaceCarrierApplication = useCallback(
    async (motorCarrierDotNumber: number, motorCarrierMcNumber: number | null) => {
      const { carrierApplication: mpCarrierApplication } = await fetchCarrierApplicationData(
        fetchMarketplaceCarrierApplication(motorCarrierDotNumber, motorCarrierMcNumber)
      )
      setCarrierApplication(mpCarrierApplication)
    },
    [fetchCarrierApplicationData]
  )

  const signMarketplaceLoadRateCon = useCallback(
    async (
      data: TSignRateConfirmationBody,
      id: string,
      setRateErrorState: (state: boolean) => void
    ) => {
      try {
        const { shipment } = await fetchData(updateMarketplaceLoadRateCon(data, id))

        if (shipment) {
          setShipmentDetails(shipment)
          setRateConSigned(true)
          setRateErrorState(false)
        } else {
          setRateErrorState(true)
        }
      } catch (e) {
        setUpdateError(e)
        setRateConSigned(false)
        setRateErrorState(true)
      }
    },
    [fetchData]
  )

  const getMarketplaceShipmentCarrierCompliance = useCallback(
    async (id: number, motorCarrierDotNumber: number, motorCarrierMcNumber: number | null) => {
      const mpCarrierCompliance = await fetchShipmentCarrierComplianceData(
        checkMarketplaceShipmentCarrierCompliance(id, motorCarrierDotNumber, motorCarrierMcNumber)
      )
      setCarrierCompliance(mpCarrierCompliance)
      return mpCarrierCompliance
    },
    [fetchShipmentCarrierComplianceData]
  )

  const resetCarrierApplication = useCallback(() => {
    setCarrierApplication(null)
  }, [])

  const resetMarketplaceShipmentCarrierCompliance = useCallback(
    () => setCarrierCompliance(null),
    []
  )

  const resetShipmentDetails = useCallback(() => {
    setShipmentDetails({} as IShipment)
  }, [setShipmentDetails])

  const updateTractorVin = useCallback(
    async (id: string, marketplaceId: string, tractorVin: string): Promise<void> => {
      try {
        await updateTractor(id, tractorVin)
      } catch (e) {
        setUpdateError(e)
      }
      await getMarketplaceLoad(marketplaceId)
    },
    [getMarketplaceLoad]
  )

  // Bidding
  const submitMarketplaceBid = useCallback(
    (bid: IMarketplaceBid) => {
      return createMarketplaceBidMutation({ bid })
    },
    [createMarketplaceBidMutation]
  )

  const editMarketplaceBid = useCallback(
    (bid: IMarketplaceBid, bidId: number) => {
      return updateMarketplaceBidMutation(
        { bid },
        {
          pathParams: {
            id: bidId,
          },
        }
      )
    },
    [updateMarketplaceBidMutation]
  )

  const removeMarketplaceBid = useCallback(
    async (bidId: string) => fetchData(deleteMarketplaceBid(bidId)),
    [fetchData]
  )

  /**
   * Re-fetches data for current shipment in shipmentDetails
   */
  const refetchShipment = useCallback(async () => {
    const { id } = shipmentDetails || {}
    if (typeof id === 'undefined') return
    await getMarketplaceLoad(`TFX-${id}`)
  }, [getMarketplaceLoad, shipmentDetails])

  const addAttachments = useCallback(
    async (...args: Parameters<typeof Api.addAttachments>) => {
      const addAttachmentFn = fmsCombinePodFiles
        ? Api.addAttachmentWithCombinedPods
        : Api.addAttachments
      const req = addAttachmentFn(...args)
      const { shipment } = await fetchData(req)

      // Update marketplace shipmentDetails
      await refetchShipment()

      // Return `shipment` so we can update Redux, if necessary
      return shipment
    },
    [fetchData, refetchShipment, fmsCombinePodFiles]
  )

  const replaceAttachment = useCallback(
    async (...args: Parameters<typeof Api.replaceAttachment>) => {
      const replaceAttachmentFn = fmsCombinePodFiles
        ? Api.replaceAttachmentWithCombinedPods
        : Api.replaceAttachment
      const req = replaceAttachmentFn(...args)
      const { shipment } = await fetchData(req)

      await refetchShipment()

      // Return `shipment` so we can update Redux, if necessary
      return shipment
    },
    [fetchData, refetchShipment, fmsCombinePodFiles]
  )

  const deleteAttachment = useCallback(
    async (...args: Parameters<typeof Api.deleteAttachment>) => {
      const req = Api.deleteAttachment(...args)
      const { shipment } = await fetchData(req)

      await refetchShipment()

      // Return `shipment` so we can update Redux, if necessary
      return shipment
    },
    [fetchData, refetchShipment]
  )

  const updateShipmentAssignment = useCallback(
    async (loadId: string | number, updatedProps: IUpdatedAssignmentProps) => {
      try {
        const req = updateAssignment(loadId, updatedProps)
        const { shipment } = await fetchData(req)

        // fetch on success
        await refetchShipment()

        // update redux
        if (shipment?.id) {
          dispatch(updateLoadAssignmentSuccess(shipment))
        }

        return shipment
      } catch (e) {
        return undefined
      }
    },
    [dispatch, fetchData, refetchShipment]
  )

  const isLoadUnavailable = errors === LOAD_UNAVAILABLE_MSG

  return {
    isLoadUnavailable,
    isLoading,
    isLoadingCarrierApplication,
    isLoadingShipmentCarrierCompliance,
    errors,
    getMarketplaceLoad,
    shipmentDetails,
    setShipmentDetails,
    signMarketplaceLoadRateCon,
    rateConSigned,
    carrierApplication,
    getMarketplaceCarrierApplication,
    updateTractorVin,
    updateError,
    activeShipmentDetailsId,
    setActiveShipmentDetailsId,
    addAttachments,
    replaceAttachment,
    deleteAttachment,
    carrierCompliance,
    getMarketplaceShipmentCarrierCompliance,
    driverLocation,
    resetCarrierApplication,
    resetMarketplaceShipmentCarrierCompliance,
    resetShipmentDetails,
    submitMarketplaceBid,
    editMarketplaceBid,
    removeMarketplaceBid,
    activeOpsRep,
    setActiveOpsRep,
    updateShipmentAssignment,
  }
}
